import { Link, createRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useBillingStatus, useSyncBillingToHelper } from '../api/billing';
import { rootRoute } from './__root';

export const billingSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/success',
  component: BillingSuccessPage,
});

// Poll for ~30s to give Stripe's webhook time to land. Most successful
// checkouts resolve within 2-3s, but a delayed webhook is rare-but-possible.
const POLL_MS = 2_000;
const MAX_ATTEMPTS = 15;

function BillingSuccessPage() {
  const status = useBillingStatus();
  const sync = useSyncBillingToHelper();
  const [attempts, setAttempts] = useState(0);
  const [synced, setSynced] = useState(false);

  const hasLicense = status.data?.has_perpetual_license ?? false;
  const subTier = status.data?.sub_tier ?? 'free';
  const isPaid = hasLicense || subTier !== 'free';

  useEffect(() => {
    if (isPaid || attempts >= MAX_ATTEMPTS) return;
    const t = setTimeout(() => {
      setAttempts((n) => n + 1);
      void status.refetch();
    }, POLL_MS);
    return () => clearTimeout(t);
  }, [isPaid, attempts, status]);

  // Once the backend reports the upgrade, push it to the helper's cache so
  // the trial gate flips off immediately on the user's machine.
  // Flip `synced` optimistically BEFORE firing the mutation — useMutation
  // returns a fresh result object on every render, so depending on `sync`
  // here would loop the effect indefinitely while the request is in flight.
  useEffect(() => {
    if (isPaid && !synced) {
      setSynced(true);
      sync.mutate(undefined, { onError: () => setSynced(false) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaid, synced]);

  const stalled = !isPaid && attempts >= MAX_ATTEMPTS;

  return (
    <section style={{ maxWidth: 640, margin: '40px auto', textAlign: 'center' }}>
      {!isPaid && !stalled && (
        <>
          <h1>Finalizing your purchase…</h1>
          <p style={{ color: '#666' }}>
            Stripe is confirming the payment. This usually takes a few seconds.
          </p>
          <div style={{ marginTop: 24, color: '#888', fontSize: 13 }}>
            Attempt {attempts + 1} / {MAX_ATTEMPTS}
          </div>
        </>
      )}

      {isPaid && (
        <>
          <h1>🎉 You're set.</h1>
          {hasLicense && (
            <p style={{ color: '#666' }}>
              Your desktop license is active. The trial notice will disappear from the helper after
              a moment.
            </p>
          )}
          {subTier !== 'free' && (
            <p style={{ color: '#666' }}>Your cloud subscription is active.</p>
          )}
          <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Link
              to="/pair"
              style={{
                padding: '10px 16px',
                background: '#2563eb',
                color: 'white',
                textDecoration: 'none',
                borderRadius: 6,
              }}
            >
              Go to Helper
            </Link>
            <Link
              to="/settings"
              style={{
                padding: '10px 16px',
                border: '1px solid #ccc',
                color: '#333',
                textDecoration: 'none',
                borderRadius: 6,
              }}
            >
              Manage billing
            </Link>
          </div>
          {sync.error && (
            <p style={{ color: 'crimson', marginTop: 16 }}>
              We confirmed the purchase, but couldn't reach the helper to refresh its cache. Pair
              the helper or restart Chrome and it'll pick up the change on its next status fetch.
            </p>
          )}
        </>
      )}

      {stalled && (
        <>
          <h1>Still finalizing…</h1>
          <p style={{ color: '#666' }}>
            The webhook is taking longer than usual. Your payment was likely successful — please
            refresh in a minute or check your email for the receipt.
          </p>
          <button
            type="button"
            onClick={() => {
              setAttempts(0);
              void status.refetch();
            }}
            style={{
              marginTop: 16,
              padding: '10px 16px',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 6,
            }}
          >
            Try again
          </button>
        </>
      )}
    </section>
  );
}
